// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertCircle from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import { loadingDotsStyles } from "@store/Themes/common/mixins/typography";
import constants from "@store/constants";
import tryJsonParse from "@store/Framework/Common/Utilities/tryJsonParse";
import { FullTruckLoadModel } from "@store/Framework/Types/ApiModels";
import Truck from "@store/Library/Components/Icons/Truck";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import React, { useEffect, useState, FC, useContext } from "react";
import { css } from "styled-components";

export interface ProductFullTruckLoadPresentationProps extends ExpandStyles<ProductFullTruckLoadStyles> {
    displayIcons?: boolean;
    displayTexts?: boolean;
    displaySeparator?: boolean;
    displayInfoTooltip?: boolean;
    overrideFtl?: FullTruckLoadModel;
}

export interface ProductFullTruckLoadProps extends ProductFullTruckLoadPresentationProps {
    overrideFtl?: FullTruckLoadModel;
}

interface FullTruckLoadMessage {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export interface ProductFullTruckLoadStyles {
    loadingText?: TypographyPresentationProps;
    fullTruckLoadMessage?: FullTruckLoadMessage;
    additionalInfoLabel?: TypographyPresentationProps;
    additionalInfoText?: InjectableCss;
    ftlWrapper?: InjectableCss;
    ftlIcon?: TooltipPresentationProps;
    pipeStyles?: TypographyPresentationProps;
}

export const productFullTruckLoadStyles: ProductFullTruckLoadStyles = {
    loadingText: {
        css: loadingDotsStyles(),
    },
    fullTruckLoadMessage: {
        text: {
            css: css`
                font-size: ${x => x.theme.design.typography.size.smaller};
                text-wrap: auto;
            `,
        },
        tooltip: {
            iconProps: {
                size: 16,
                src: AlertCircle,
            },
            mergeCss: true,
            cssOverrides: {
                tooltipWrapper: css`
                    margin: 0;
                `,
            },
        },
    },
    ftlWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(4)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    additionalInfoLabel: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    ftlIcon: {
        iconProps: {
            size: 20,
            src: Truck,
            color: "inherit",
        },
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                margin: 0;
                text-wrap: auto;
            `,
        },
    },
    additionalInfoText: {
        css: css`
            display: inherit;
            align-items: inherit;
            gap: inherit;
        `,
    },
    pipeStyles: {
        color: "text.disabled",
        css: css`
            margin: ${pxToRem(0, 6)};
        `,
    },
};

const ProductFullTruckLoad: FC<ProductFullTruckLoadProps> = ({
    extendedStyles,
    overridedStyles,
    displayIcons = false,
    displayTexts = true,
    displaySeparator = false,
    displayInfoTooltip = true,
    overrideFtl,
}) => {
    const styles = useExpandProps(productFullTruckLoadStyles, extendedStyles, overridedStyles);
    const { product, productInfo } = useContext(ProductContext);
    const isProductContext = Boolean(!overrideFtl && product && productInfo);
    const isProductPage = useCurrentPage().type === constants.pageType.productDetails;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fullTruckLoad, setFullTruckLoad] = useState<FullTruckLoadModel>();
    const [fullTruckLoadData] = useState<FullTruckLoadModel[]>([]);
    const displayIcon = isProductPage ? true : displayIcons;

    let fullTruckLoads;
    let pricing: ProductPriceDto | undefined;
    let defaultPlantNumber: string | undefined;
    let fullTruckLoadObject: any;

    if (isProductContext) {
        fullTruckLoads = product.properties.fullTruckLoads;
        pricing = productInfo.pricing;
        defaultPlantNumber = pricing?.additionalResults?.warehouseName;

        // Receiving data as a JSON string, needs to convert and extract properties
        fullTruckLoadObject = tryJsonParse(fullTruckLoads);
    }

    useEffect(() => {
        if (isProductContext) {
            setIsLoading(!pricing);
        }
    }, [pricing, defaultPlantNumber]);

    useEffect(() => {
        if (isProductContext) {
            if (fullTruckLoadObject) {
                fullTruckLoadObject.forEach((fullTruckLoad: any) =>
                    fullTruckLoadData.push({
                        plant: fullTruckLoad.Plant,
                        ftlQuantity: fullTruckLoad.FullTruckLoad,
                    }),
                );
            }
        }
    }, [fullTruckLoadObject]);

    useEffect(() => {
        if (isProductContext) {
            setFullTruckLoad(
                defaultPlantNumber ? fullTruckLoadData.find(x => x.plant === defaultPlantNumber) : fullTruckLoadData[0],
            );
        }
    }, [defaultPlantNumber]);

    useEffect(() => {
        if (overrideFtl) {
            setFullTruckLoad(overrideFtl);
        }
    }, [overrideFtl]);

    if (fullTruckLoadObject?.length === 0 && isProductContext) {
        return null;
    }

    return (
        <>
            {displaySeparator && <Typography {...styles.pipeStyles}>|</Typography>}
            <StyledWrapper {...styles.ftlWrapper} data-test-selector="product_full_truck_load">
                {displayIcon && (
                    <Tooltip {...styles.ftlIcon} text={siteMessage("ProductInfo_FtlIconMessage") as string} />
                )}
                {displayTexts && (
                    <Typography {...styles.additionalInfoLabel}>
                        {`${translate("Full Truck Load")}${translate(":")}`}
                    </Typography>
                )}
                <StyledWrapper {...styles.additionalInfoText}>
                    {isLoading ? (
                        <Typography {...styles.loadingText}>&nbsp;</Typography>
                    ) : (
                        ` ${fullTruckLoad?.ftlQuantity || translate("N/A")}`
                    )}
                    {!isLoading && displayInfoTooltip && (
                        <Tooltip
                            {...styles.fullTruckLoadMessage?.tooltip}
                            text={siteMessage("ProductInfo_FtlMessage") as string}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

export default ProductFullTruckLoad;
